/**
 * What the loaded bundle actually carries.
 *
 * The app was written against a workbook export. It now runs on the curated lakehouse
 * model, which carries a different set of grains: the open-claims worklist is not pulled
 * at all, and collected % is not in the curated model. Every measure built on those
 * returns null, and null rendered as "0" or "—" is a lie in two different directions —
 * "0 open claims" reads as an empty pile, and a bare dash reads as a rendering fault.
 *
 * So absence is computed ONCE, here, from the bundle itself rather than asserted in
 * prose on each page. When the pipeline starts producing a grain, every notice built on
 * this disappears on its own; nothing has to be found and deleted.
 */
import type { Dataset } from './types'

export type GrainKey =
  | 'worklist'
  | 'worklistRecency'
  | 'worklistAge'
  | 'collectedPct'
  | 'shoreMix'
  | 'claimRows'
  | 'touchRows'

export interface Coverage {
  /** The open pile: how many claims are open per person. */
  worklist: boolean
  /**
   * The RECENCY half of the worklist: not-worked-this-month counts and the 14/30-day shares.
   *
   * Tracked separately from `worklist` because the grain does not arrive whole. The
   * 2026-09-17 bundle carries a real `openClaims` for 399 people — 171,875 claims — and
   * zero on every one of `claimsNotWorked`, `pctWorkedLast30` and `pctWorkedLast14`.
   * Treating the worklist as one boolean made the app announce "Never worked 0 (0.0%)"
   * and "Worked in 30: 0.0%" — one of which says nothing is stale and the other says
   * nothing has been touched, and both are assertions the data never made.
   */
  worklistRecency: boolean
  /**
   * The pile by when it was last touched: never worked, and worked in the last 14
   * days / 15–30 days / over 30 days. Present from 2026-10-01; earlier bundles have
   * no such fields, and a missing field must read as unknown, not as zero never
   * worked.
   */
  worklistAge: boolean
  /** Collected %, on any person at all. */
  collectedPct: boolean
  /**
   * Whether shore varies WITHIN a person's claims.
   *
   * It does not: shore is a property of the person, not of the claim, so every
   * composition row is 100% one shore. A per-person shore-mix visual would be a single
   * full bar on every row, and the shore filter is an exact filter, not an allocation.
   */
  shoreMix: boolean
  /** One row per billed claim. */
  claimRows: boolean
  /** One row per touch. */
  touchRows: boolean
}

/**
 * The sentence each absence gets on screen. One place, so the Backlog page and a stat
 * tile's tooltip cannot drift apart.
 */
export const ABSENT: Record<GrainKey, { what: string; why: string }> = {
  worklist: {
    what: 'The open-claims grain is not in this bundle.',
    why: 'Open claims, claims not worked this month and how recently the pile was worked all come from the open worklist fact, which this pipeline does not pull. Read every backlog figure as unknown — not as zero.',
  },
  worklistRecency: {
    what: 'Worklist recency is not in this bundle.',
    why: 'The open-claims count is loaded, but claims not worked this month is zero on every row of it — the pipeline carries the size of the pile and not its age. A zero here would say nothing is stale, which the data does not say.',
  },
  worklistAge: {
    what: 'When the open claims were last worked is not in this bundle.',
    why: 'The four last-worked buckets need each open claim joined to its last touch, which bundles built before 1 October 2026 do not carry. Read them as unknown — not as zero.',
  },
  collectedPct: {
    what: 'Collection rate is not in this bundle.',
    why: 'This bundle carries no bill-month charge history, which the 6-month, 3-month-lag window is read from — it was built before 5 October 2026 or the charge was not read. Nothing here can be ranked, averaged or scored on it.',
  },
  shoreMix: {
    what: 'Shore does not vary within a person.',
    why: 'Shore is a property of the employee, not of the claim, so every person is 100% one shore. A shore mix would be a single full bar on every row; the shore filter selects people instead.',
  },
  claimRows: {
    what: 'Claim-level rows are not in this bundle.',
    why: 'Every page is aggregated from employee, month and crosstab grains. The per-claim grain has not been produced.',
  },
  touchRows: {
    what: 'Touch-level rows are not in this bundle.',
    why: 'Touch counts arrive pre-aggregated to month × employee. The per-touch grain has not been produced.',
  },
}

/**
 * A numeric field is "carried" only if some row has a non-zero value for it.
 *
 * The rule is deliberately blunt, and it is the same one `collectedPct` uses: a column
 * that is empty on every row of a populated table was not produced. It can in principle
 * be fooled by a genuinely all-zero column — but a genuine all-zero column is itself
 * something the reader should be told about rather than shown as a confident 0.
 */
const carried = <T>(rows: readonly T[], get: (r: T) => number | null | undefined) =>
  rows.some((r) => {
    const v = get(r)
    return v !== null && v !== undefined && v !== 0
  })

export function coverageOf(d: Dataset): Coverage {
  return {
    worklist: carried(d.worklist, (w) => w.openClaims),
    worklistRecency:
      carried(d.worklist, (w) => w.claimsNotWorked) ||
      carried(d.worklist, (w) => w.pctWorkedLast30) ||
      carried(d.worklist, (w) => w.pctWorkedLast14),
    // Presence, not "carried": a whole pile worked in the last fortnight is a real
    // zero never-worked, and the four counts arrive together or not at all.
    worklistAge:
      carried(d.worklist, (w) => w.openClaims) &&
      d.worklist.some((w) => w.neverWorked !== undefined && w.neverWorked !== null),
    // The lagged window needs the bill-month series; a bundle without it carries the
    // superseded month-of-bills rate, which must not show under the new label.
    collectedPct: d.employees.some(
      (e) => e.collectionMonths !== undefined && e.collectedPct !== null,
    ),
    // Only true if some person's claims genuinely straddle both shores.
    shoreMix: d.composition.some((c) => Object.keys(c.byShore).length > 1),
    claimRows: (d.claimRows?.length ?? 0) > 0,
    touchRows: (d.touchRows?.length ?? 0) > 0,
  }
}

/** Shore values present across the population — the shore filter's real options. */
export function shoresIn(d: Dataset): string[] {
  return [...new Set(d.employees.map((e) => e.shore).filter((s): s is 'On' | 'Off' => s !== null))].sort()
}
